import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Chip,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import KeyIcon from '@mui/icons-material/Key';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BananaContext } from '@wikimedia/react.i18n';

const APIKeyConfig = ({
  open,
  onClose,
  onSave,
  initialProvider = 'openai',
  chatService,
}) => {
  const theme = useTheme();
  const translator = useContext(BananaContext);
  const [provider, setProvider] = useState(initialProvider);
  const [originalProvider, setOriginalProvider] = useState(initialProvider);
  const [apiKey, setApiKey] = useState('');
  const [hasKey, setHasKey] = useState(false);
  const [error, setError] = useState('');
  const [isNewKey, setIsNewKey] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  const modelVersions = {
    openai: 'gpt-4-turbo',
    anthropic: 'claude-4-sonnet-20250514',
  };

  const translations = {
    ai_chat_api_config: translator.i18n('ai_chat_api_config'),
    ai_chat_choose_provider: translator.i18n('ai_chat_choose_provider'),
    ai_chat_openai_info: translator.i18n('ai_chat_openai_info'),
    ai_chat_anthropic_info: translator.i18n('ai_chat_anthropic_info'),
    ai_chat_api_key: translator.i18n('ai_chat_api_key'),
    ai_chat_openai_info_tooltip: translator.i18n('ai_chat_openai_info_tooltip'),
    ai_chat_anthropic_info_tooltip: translator.i18n(
      'ai_chat_anthropic_info_tooltip'
    ),
    ai_chat_api_key_privacy: translator.i18n('ai_chat_api_key_privacy'),
    ai_chat_cancel: translator.i18n('ai_chat_cancel'),
    ai_chat_save: translator.i18n('ai_chat_save'),
    ai_chat_api_key_clear: translator.i18n('ai_chat_api_key_clear'),
    ai_chat_api_key_required: translator.i18n('ai_chat_api_key_required'),
    ai_chat_provider: translator.i18n('ai_chat_provider'),
    ai_chat_provider_chatgpt: translator.i18n('ai_chat_provider_chatgpt'),
    ai_chat_provider_claude: translator.i18n('ai_chat_provider_claude'),
  };

  const providerOptions = [
    { value: 'openai', label: translations.ai_chat_provider_chatgpt || 'ChatGPT' },
    { value: 'anthropic', label: translations.ai_chat_provider_claude || 'Claude' },
  ];

  useEffect(() => {
    if (open) {
      const currentProvider = chatService.getProvider();

      setProvider(currentProvider);
      setOriginalProvider(currentProvider);

      const keyStorage = currentProvider === 'openai' 
        ? 'paybyrd_openai_api_key' 
        : 'paybyrd_anthropic_api_key';
      
      const apiKeyFromStorage = localStorage.getItem(keyStorage);
      const keyExists = !!apiKeyFromStorage;
      
      setHasKey(keyExists);

      setApiKey(keyExists ? '••••••••••••••••••••••••••' : '');
      setIsNewKey(false);
      setError('');
    }
  }, [open, chatService]);

  const handleProviderChange = (event) => {
    const newProvider = event.target.value;
    setProvider(newProvider);
    setError('');

    const keyStorage = newProvider === 'openai' 
      ? 'paybyrd_openai_api_key' 
      : 'paybyrd_anthropic_api_key';
    
    const apiKeyFromStorage = localStorage.getItem(keyStorage);
    const keyExists = !!apiKeyFromStorage;
    
    setHasKey(keyExists);
    setApiKey(keyExists ? '••••••••••••••••••••••••••' : '');
    setIsNewKey(false);
  };
  
  const handleApiKeyChange = (e) => {
    const newValue = e.target.value;
    setApiKey(newValue);
    setError('');
    setIsNewKey(newValue !== '••••••••••••••••••••••••••');
  };

  const handleSave = () => {
    const needsApiKey = !hasKey && !apiKey;
    
    if (needsApiKey) {
      setError(translations.ai_chat_api_key_required);
      return;
    }

    try {
      chatService.setProvider(provider);

      if (isNewKey && apiKey) {
        chatService.setApiKey(apiKey);
      }

      chatService.setModel(modelVersions[provider]);

      onSave && onSave(provider);
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };
  
  const isSaveEnabled = () => {
    if (provider !== originalProvider) return true;
    
    if (isNewKey && apiKey) return true;
    
    if (!hasKey && apiKey) return true;
    
    return false;
  };

  const handleClear = async () => {
    setIsClearing(true);
    try {
      chatService.setProvider(provider);

      chatService.clearApiKey();

      setHasKey(false);
      setApiKey('');
      setIsNewKey(false);

      await new Promise(resolve => setTimeout(resolve, 300));
      
      onSave && onSave(provider);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setError('');
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          color: 'text.primary'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6" component="span">
            {translations.ai_chat_api_config}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={() => {
            setError('');
            onClose();
          }}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} variant="filled">
            {error}
          </Alert>
        )}

        <Card elevation={0} sx={{ mb: 3, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'divider' : 'grey.200' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {translations.ai_chat_choose_provider}
              </Typography>
            </Box>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={provider}
                onChange={handleProviderChange}
                displayEmpty
                sx={{ 
                  bgcolor: 'background.paper', 
                  border: 'none',
                  '& .MuiSelect-select': {
                    color: 'text.primary'
                  }
                }}
                placeholder={translations.ai_chat_provider}
                MenuProps={{
                  PaperProps: {
                    elevation: 3,
                    sx: {
                      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                      color: 'text.primary',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      padding: 0,
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        padding: '8px 16px',
                        minHeight: '48px'
                      }
                    }
                  }
                }}
              >
                {providerOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value} 
                    sx={{ 
                      bgcolor: 'transparent',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        bgcolor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.16)' 
                          : 'rgba(0, 0, 0, 0.08)',
                        color: 'text.primary'
                      },
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)'
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.24)'
                          : 'rgba(0, 0, 0, 0.12)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        <Typography sx={{ color: 'inherit' }}>
                          {option.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.7 }}>
                        {modelVersions[option.value]}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ mb: 3, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'divider' : 'grey.200' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {translations.ai_chat_api_key}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {provider === 'openai'
                  ? translations.ai_chat_openai_info
                  : translations.ai_chat_anthropic_info}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Model: <strong>{modelVersions[provider]}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                type="password"
                value={apiKey}
                onChange={handleApiKeyChange}
                placeholder={provider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                variant="outlined"
                size="medium"
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                }}
                sx={{ bgcolor: 'background.paper', border: 'none' }}
              />
              <Tooltip
                title={
                  provider === 'openai'
                    ? translations.ai_chat_openai_info_tooltip
                    : translations.ai_chat_anthropic_info_tooltip
                }
              >
                <IconButton size="medium">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ px: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InfoIcon fontSize="small" sx={{ fontSize: 16 }} />
            {translations.ai_chat_api_key_privacy}
          </Typography>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        {hasKey && (
          <Button 
            variant="outlined" 
            color="error" 
            onClick={handleClear}
            startIcon={<CloseIcon />}
            size="medium"
            disabled={isClearing}
            sx={{
              borderColor: theme => theme.palette.error.main,
              '&:hover': {
                borderColor: theme => theme.palette.error.dark
              }
            }}
          >
            {translations.ai_chat_api_key_clear}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button 
          onClick={() => {
            setError('');
            onClose();
          }} 
          color="inherit"
          size="medium"
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {translations.ai_chat_cancel}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!isSaveEnabled()}
          startIcon={<CheckCircleIcon />}
          size="medium"
          sx={{
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme => theme.palette.primary.dark
            }
          }}
        >
          {translations.ai_chat_save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default APIKeyConfig;
